import { TaskStatus } from '@edgebox/sync-core-data-definitions';
import type { MonitoringPromptRunControllerCountAnswerExportRequest } from '@edgebox/sync-core-rest-client/dist/services/api/apis';
import type { MigrationSummary, StartAnswerExportDto } from '@edgebox/sync-core-rest-client/dist/services/api/models';

// Only types are pulled from the generated client, so this module carries no REST dependency at
// runtime and stays unit-testable in isolation. `StartAnswerExportDto` is the export's own request
// body: the modal edits one directly rather than restating its shape.

/** How often the modal asks the task group where it is once an export is running. */
export const ANSWER_EXPORT_POLL_INTERVAL_MS = 3_000;

/** Debounce before a filter edit turns into a live-count request, so typing a window is one call. */
export const ANSWER_EXPORT_COUNT_DEBOUNCE_MS = 400;

/** Where an export sits, derived from the task group's own status. */
export type AnswerExportPhase = 'queued' | 'running' | 'ready' | 'failed';

/** Progress across the group's tasks, for the running state's bar and count. */
export interface AnswerExportProgress {
  done: number;
  total: number;
  percent: number;
}

/**
 * The page offers one engine, one locale and one prompt at a time; the export takes lists. Seed the
 * export's request from those single choices, so the default export is what the reader is looking at.
 *
 * Every include flag starts OFF, and the server defaults agree. The full answer text is the bulk of
 * the archive by a wide margin, and untracked subjects are the long noisy tail; someone who wants
 * either can say so, but neither belongs in the export a reader gets by pressing the button.
 */
export function seedExportDto(pageFilters: { engineKey?: string; localeKey?: string; promptKey?: string }): StartAnswerExportDto {
  const one = (value?: string): string[] | null => {
    const trimmed = String(value ?? '').trim();
    return trimmed ? [trimmed] : null;
  };
  return {
    from: null,
    to: null,
    engines: one(pageFilters.engineKey),
    countryLocales: one(pageFilters.localeKey),
    prompts: one(pageFilters.promptKey),
    includeAnswerText: false,
    includePromptsWithoutAnswers: false,
    includeUntrackedSubjects: false,
  };
}

function cleanList(values?: string[] | null): string[] | null {
  const kept = (values ?? []).map((value) => String(value ?? '').trim()).filter((value) => value.length > 0);
  return kept.length ? kept : null;
}

/**
 * The request body to start the export: empty lists collapse to `null` so an unset dimension reads as
 * "no filter" rather than "match nothing", and the window is carried only when the reader set it.
 */
export function normalizeStartDto(dto: StartAnswerExportDto): StartAnswerExportDto {
  return {
    from: typeof dto.from === 'number' ? dto.from : null,
    to: typeof dto.to === 'number' ? dto.to : null,
    engines: cleanList(dto.engines),
    countryLocales: cleanList(dto.countryLocales),
    prompts: cleanList(dto.prompts),
    includeAnswerText: dto.includeAnswerText ?? false,
    includePromptsWithoutAnswers: dto.includePromptsWithoutAnswers ?? false,
    includeUntrackedSubjects: dto.includeUntrackedSubjects ?? false,
  };
}

/**
 * The live-count query for the current filters. The count is the exact number of answer rows the
 * export will carry, so it takes the same window and list filters — the include flags do not change
 * that row count and are not sent. Lists are the comma-separated form the endpoint reads from the
 * query string.
 */
export function toCountRequest(dto: StartAnswerExportDto): MonitoringPromptRunControllerCountAnswerExportRequest {
  const csv = (values?: string[] | null): string | undefined => {
    const kept = cleanList(values);
    return kept ? kept.join(',') : undefined;
  };
  return {
    from: typeof dto.from === 'number' ? String(dto.from) : undefined,
    to: typeof dto.to === 'number' ? String(dto.to) : undefined,
    engines: csv(dto.engines),
    countryLocales: csv(dto.countryLocales),
    prompts: csv(dto.prompts),
  };
}

/** Map a task group's status onto the four states the modal shows. */
export function exportPhaseFromStatus(status?: TaskStatus | string | null): AnswerExportPhase {
  switch (status) {
    case TaskStatus.Finished:
    case TaskStatus.Skipped:
      return 'ready';
    case TaskStatus.Failed:
    case TaskStatus.Aborted:
    case TaskStatus.LimitExceeded:
      return 'failed';
    case TaskStatus.Running:
    case TaskStatus.Retrying:
      return 'running';
    default:
      return 'queued';
  }
}

const COMPLETED_TASK_STATUSES: ReadonlySet<string> = new Set([TaskStatus.Finished, TaskStatus.Skipped]);

/**
 * Chunk progress from the group summary: how many of its tasks have finished, out of the total. A
 * failed export is reported by its status, not by this bar, so failed tasks count only toward the
 * total.
 */
export function exportProgressFromSummary(summary?: Pick<MigrationSummary, 'byStatus'> | null): AnswerExportProgress {
  const buckets = summary?.byStatus ?? [];
  let done = 0;
  let total = 0;
  for (const bucket of buckets) {
    const count = bucket?.count ?? 0;
    total += count;
    if (COMPLETED_TASK_STATUSES.has(bucket?.status)) {
      done += count;
    }
  }
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  return { done, total, percent };
}

/**
 * The action label. It names the exact count once it is known and never a guessed one: while the
 * count is in flight, or if it could not be taken, the button reads plainly and does not promise a
 * number the archive would not match.
 */
export function exportCountButtonLabel(count: number | undefined, counting: boolean): string {
  if (counting || typeof count !== 'number') {
    return 'Export answers';
  }
  if (count === 1) {
    return 'Export 1 answer';
  }
  return `Export ${count.toLocaleString()} answers`;
}

/** A day-boundary epoch for the window controls: `start` is that day's first ms, `end` its last. */
export function dateInputToEpochMs(value: string, boundary: 'start' | 'end'): number | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const base = Date.parse(`${trimmed}T00:00:00.000Z`);
  if (Number.isNaN(base)) {
    return null;
  }
  if (boundary === 'end') {
    const MS_PER_DAY = 24 * 60 * 60 * 1_000;
    return base + MS_PER_DAY - 1;
  }
  return base;
}

/** The `type="date"` value (UTC `YYYY-MM-DD`) for an epoch, or empty when unset. */
export function epochMsToDateInput(epochMs?: number | null): string {
  if (typeof epochMs !== 'number' || Number.isNaN(epochMs)) {
    return '';
  }
  return new Date(epochMs).toISOString().slice(0, 10);
}
